import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  useCreateAdyenPaymentSession,
  useSubmitAdyenPaymentResult,
  useSubmitPaymentWithStoredMethod,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useApplyExternalPayment } from 'src/hooks/api/useApplyExternalPayment';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import { useUpsertBuyer } from 'src/hooks/api/useUpsertBuyer';
import { roundToPrecision } from 'src/utils/numberFormatter';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ApiException,
  BuyerUpsertDto,
  BuyerWithSalesDto,
  CreateRetailSaleWithPaymentResponse,
  EventWithData,
  GenericAddressDto,
  ListingDetails,
  SaleClient,
  SaleLineItemCoverage,
  Tag,
} from 'src/WebApiController';

import {
  buildRetailSaleGroupInput,
  buildRetailSaleInput,
  computeProportionalCoverages,
  getEffectiveListingIds,
  getPaymentAmount,
  getPaymentCountryCode,
  getRetailMerchantReferenceId,
} from './RetailSaleLocalContext.utils';
import { RetailSaleFormValues } from './types';

/**
 * POS-8322: Apply form tags to all created sales. Uses SaleClient.updateSaleBySaleId
 * with the tags wrapped in a PosField (hasChanged: true). Best-effort.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyTagsToSales = async (
  tags: Tag[] | null | undefined,
  saleIds: number[],
  config: any
) => {
  if (!tags || tags.length === 0 || saleIds.length === 0) return;
  const tagsWithValues = tags.filter((t) => t.value);
  if (tagsWithValues.length === 0) return;
  const saleClient = new SaleClient(config);
  for (const saleId of saleIds) {
    try {
      await saleClient.updateSaleBySaleId(saleId, {
        saleId,
        tags: { value: tagsWithValues, hasChanged: true },
      } as any);
    } catch (e) {
      console.error('applyTagsToSales failed for sale', saleId, e);
    }
  }
};

/**
 * Post-sale navigation resolver. Every retail sale is now created as a
 * SalesGroup (single or multi); the response's `sales.length` tells us
 * which case we're in and thus where to route:
 *
 *  - length === 1: single-listing sale → navigate to the sale detail
 *    modal (legacy behavior).
 *  - length > 1: multi-listing sale (POS-8649) → navigate to /sales with
 *    the salesGroupId query param so the SalesGroupModal (POS-8632)
 *    picks the group up.
 *
 * Only the multi-listing branch depends on the SalesGroupModal being
 * merged. Until POS-8632 lands, the multi-listing case still routes
 * correctly by URL — the modal just isn't there to open yet.
 */
export const resolvePostSaleNavigation = (
  sales: CreateRetailSaleWithPaymentResponse[] | undefined,
  // POS-9097: salesGroupId is null for a TRUE single-listing sale (no group-of-one).
  salesGroupId: string | null
): string => {
  // POS-9097: a single-listing retail sale has no SalesGroup — route to its own sale
  // detail view rather than the group deep-link (which would produce a null group id).
  if (!salesGroupId) {
    const saleId = sales?.[0]?.saleId;
    return saleId != null ? getSaleDetailsRelativeUrl(saleId) : '/sales';
  }

  // POS-8918: a multi-listing checkout opens the Sales Group modal after creation so the
  // user can immediately see the group view with payment status, invoice, and member sales.
  return `/sales?uniqueSalesGroupId=${salesGroupId}`;
};

/**
 * POS-9097: decides whether a submission should use the dedicated single-sale endpoint
 * (CreateRetailSale) rather than the multi-listing group endpoint (CreateRetailSaleGroup).
 * This is driven by the number of DISTINCT listings actually being sold — NOT by which modal
 * opened. A multi-listing checkout whose selection collapses to a single listing must use the
 * single endpoint, because the group endpoint rejects a single listing with a 400.
 */
const resolvesToSingleListing = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined
): boolean => getEffectiveListingIds(values, listing).length < 2;

export type AdyenSession = {
  saleId: number;
  salesGroupId: string | null;
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  merchantReference: string;
  countryCode: string | null;
  lineItemCoverages: SaleLineItemCoverage[] | null;
};

type UseRetailSaleOperationsParams = {
  event: EventWithData | undefined;
  listing: ListingDetails | undefined;
  selectedBuyer: BuyerWithSalesDto | undefined;
  selectedStoredPaymentMethodId: string | null;
  setSelectedBuyer: (buyer: BuyerWithSalesDto) => void;
  isPartialPayment: boolean;
  partialAmount: number;
  /**
   * POS-9099: optional completion signal for the chained "Add Retail Sale" workflow.
   * Invoked once a sale has been successfully created (every completion path navigates
   * afterwards, so this fires with the navigation). Lets the chain orchestrator mark the
   * sale committed and skip the rollback that would otherwise delete the seeded inventory.
   */
  onSaleCreated?: () => void;
};

export const useRetailSaleOperations = ({
  listing,
  selectedBuyer,
  selectedStoredPaymentMethodId,
  setSelectedBuyer,
  isPartialPayment,
  partialAmount,
  onSaleCreated,
}: UseRetailSaleOperationsParams) => {
  const { getValues } = useFormContext<RetailSaleFormValues>();
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const rawNavigate = useNavigate();

  // POS-9099: every navigation in this hook happens only after a sale has been created,
  // so route it through a wrapper that first fires onSaleCreated (the chain-completion
  // signal). Covers all completion paths (unpaid / external / Adyen) in one place, so no
  // individual success site can be missed.
  const navigate = useCallback(
    (url: string) => {
      onSaleCreated?.();
      rawNavigate(url);
    },
    [onSaleCreated, rawNavigate]
  );

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();
  const submitPaymentWithStoredMethod = useSubmitPaymentWithStoredMethod();
  const applyExternalPayment = useApplyExternalPayment();
  const upsertBuyer = useUpsertBuyer();
  const knownCountries = useKnownCountries();

  // Reference to track the created sale
  // In case of payment error, we want to avoid creating multiple sales for the same listing
  const saleIdRef = useRef<number | null>(null);
  // Every retail sale belongs to a sales group (a single sale is a group of one); Adyen
  // sessions/payments/refunds key off this group id.
  const salesGroupIdRef = useRef<string | null>(null);
  // Full sales array from the createRetailSaleGroup response. Retained across the
  // payment step so post-payment Adyen success paths can pick the right destination
  // (single-sale detail vs multi-listing /sales?salesGroupId=…) via
  // resolvePostSaleNavigation, without re-plumbing sales.length through each callback.
  const salesRef = useRef<CreateRetailSaleWithPaymentResponse[] | undefined>(
    undefined
  );

  const [adyenSession, setAdyenSession] = useState<AdyenSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const disableEditing = useMemo(() => {
    return isLoading || Boolean(adyenSession);
  }, [adyenSession, isLoading]);

  const onCompleteAsUnpaid = useCallback(() => {
    const values = getValues();
    setIsLoading(true);

    // POS-9097: a TRUE single-listing sale uses the dedicated createRetailSale
    // endpoint (the group endpoint now rejects a single listing with a 400).
    if (resolvesToSingleListing(values, listing)) {
      const input = buildRetailSaleInput(values, listing, selectedBuyer);

      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);
          // createRetailSale throws (HttpException → tryInvokeApi error handler)
          // on validation failure, so a resolved response with a truthy saleId
          // is success. The response has no `success`/`sales` fields.
          const result = await client.createRetailSale(input);

          if (result.saleId) {
            await applyTagsToSales(
              values.tags,
              [result.saleId],
              activeAccountWebClientConfig
            );
            navigate(getSaleDetailsRelativeUrl(result.saleId));
          }
        },
        (error) => {
          showErrorDialog('SaleClient.createRetailSale', error, {
            trackErrorData: input,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
      return;
    }

    const groupInput = buildRetailSaleGroupInput(
      values,
      listing,
      selectedBuyer
    );

    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);
        const result = await client.createRetailSaleGroup(groupInput);
        const saleId = result.sales?.[0]?.saleId;

        if (result.success && saleId) {
          const saleIds = result.sales?.map((s) => s.saleId) ?? [];
          await applyTagsToSales(
            values.tags,
            saleIds,
            activeAccountWebClientConfig
          );
          navigate(
            resolvePostSaleNavigation(result.sales, result.salesGroupId)
          );
        } else {
          showErrorDialog(
            'SaleClient.createRetailSaleGroup',
            {
              message: result.message ?? genericError,
              // POS-8322: Use 400 (validation error) not 500 (server error) so the
              // dialog shows the message instead of "Unexpected Error".
              status: 400,
            } as ApiException,
            { trackErrorData: groupInput }
          );
        }
      },
      (error) => {
        showErrorDialog('SaleClient.createRetailSaleGroup', error, {
          trackErrorData: groupInput,
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    genericError,
    getValues,
    listing,
    navigate,
    selectedBuyer,
    showErrorDialog,
  ]);

  /**
   * Submits the retail sale when the broker selects "Paid Externally".
   *
   * Full payment (isPartialPayment === false):
   *   Sends external payment fields directly with createRetailSaleGroup
   *   so the sale is created as Paid in a single call (Step 1 path).
   *
   * Partial payment (isPartialPayment === true):
   *   1. Creates the sale as Unpaid (no external fields on create)
   *   2. Auto-computes proportional coverages across unpaid lines
   *   3. Calls ApplyExternalPayment with the computed coverages.
   */
  const onCompleteExternalPayment = useCallback(() => {
    const values = getValues();
    setIsLoading(true);

    // POS-9097: TRUE single-listing sale → dedicated createRetailSale endpoint.
    if (resolvesToSingleListing(values, listing)) {
      if (isPartialPayment) {
        // Partial path: create the sale as Unpaid (external fields nulled), then
        // auto-compute proportional coverages over the single sale's unpaid lines
        // and apply the external payment at the SALE level.
        const input = buildRetailSaleInput(
          values,
          listing,
          selectedBuyer,
          false
        );

        tryInvokeApi(
          async () => {
            const client = new SaleClient(activeAccountWebClientConfig);
            if (!saleIdRef.current) {
              const result = await client.createRetailSale(input);
              if (!result.saleId) return;
              saleIdRef.current = result.saleId;
              await applyTagsToSales(
                values.tags,
                [result.saleId],
                activeAccountWebClientConfig
              );
            }

            // Fetch the single sale's details and auto-compute coverages over its
            // own unpaid marketplace lines (mirrors the group path, [saleDetails]).
            const saleDetails = await client.getSaleBySaleId(
              saleIdRef.current!
            );
            const roundedPartial = roundToPrecision(partialAmount, 2);
            const coverages = computeProportionalCoverages(roundedPartial, [
              saleDetails,
            ]);

            await applyExternalPayment.mutateAsync({
              saleId: saleIdRef.current!,
              input: {
                externalPaymentMethodType: values.externalPaymentMethodType!,
                externalPaymentNote: values.externalPaymentNote ?? null,
                externalPaymentAmount: roundedPartial,
                externalPaymentDate: values.externalPaymentDate ?? null,
                coverages,
              },
            });
            navigate(getSaleDetailsRelativeUrl(saleIdRef.current!));
          },
          (error) => {
            showErrorDialog('SaleClient.createRetailSale', error, {
              trackErrorData: input,
            });
          },
          () => {
            setIsLoading(false);
          }
        );
      } else {
        // Full-payment path: include external fields so the sale is created Paid.
        const input = buildRetailSaleInput(
          values,
          listing,
          selectedBuyer,
          true
        );

        tryInvokeApi(
          async () => {
            const client = new SaleClient(activeAccountWebClientConfig);
            const result = await client.createRetailSale(input);

            if (result.saleId) {
              await applyTagsToSales(
                values.tags,
                [result.saleId],
                activeAccountWebClientConfig
              );
              navigate(getSaleDetailsRelativeUrl(result.saleId));
            }
          },
          (error) => {
            showErrorDialog('SaleClient.createRetailSale', error, {
              trackErrorData: input,
            });
          },
          () => {
            setIsLoading(false);
          }
        );
      }
      return;
    }

    if (isPartialPayment) {
      // Partial path: create sale as Unpaid (if not already created),
      // then auto-compute coverages and apply external payment.
      const groupInput = buildRetailSaleGroupInput(
        values,
        listing,
        selectedBuyer,
        false
      );

      tryInvokeApi(
        async () => {
          if (!saleIdRef.current) {
            const client = new SaleClient(activeAccountWebClientConfig);
            const result = await client.createRetailSaleGroup(groupInput);
            const saleId = result.sales?.[0]?.saleId;

            if (!result.success || !saleId) {
              showErrorDialog(
                'SaleClient.createRetailSaleGroup',
                {
                  message: result.message ?? genericError,
                  status: 400,
                } as ApiException,
                { trackErrorData: groupInput }
              );
              return;
            }

            saleIdRef.current = saleId;
            salesGroupIdRef.current = result.salesGroupId;
            salesRef.current = result.sales;
            const saleIds = result.sales?.map((s) => s.saleId) ?? [];
            await applyTagsToSales(
              values.tags,
              saleIds,
              activeAccountWebClientConfig
            );
          }

          // Fetch unpaid lines and auto-compute proportional coverages
          const client = new SaleClient(activeAccountWebClientConfig);
          const salesData = await client.getSalesInSalesGroup(
            salesGroupIdRef.current!
          );
          // Round to cents so the applied amount and the per-line coverages agree.
          const roundedPartial = roundToPrecision(partialAmount, 2);
          const coverages = computeProportionalCoverages(
            roundedPartial,
            salesData
          );

          await applyExternalPayment.mutateAsync({
            salesGroupId: salesGroupIdRef.current!,
            input: {
              externalPaymentMethodType: values.externalPaymentMethodType!,
              externalPaymentNote: values.externalPaymentNote ?? null,
              customPaymentTypeName: values.customPaymentTypeName ?? null,
              externalPaymentAmount: roundedPartial,
              externalPaymentDate: values.externalPaymentDate ?? null,
              coverages,
            },
          });
          navigate(
            resolvePostSaleNavigation(
              salesRef.current,
              salesGroupIdRef.current!
            )
          );
        },
        (error) => {
          showErrorDialog('SaleClient.createRetailSaleGroup', error, {
            trackErrorData: groupInput,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    } else {
      // Step 1 full-payment path: include external fields so the sale is created Paid.
      const groupInput = buildRetailSaleGroupInput(
        values,
        listing,
        selectedBuyer,
        true
      );

      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);
          const result = await client.createRetailSaleGroup(groupInput);
          const saleId = result.sales?.[0]?.saleId;

          if (result.success && saleId) {
            const saleIds = result.sales?.map((s) => s.saleId) ?? [];
            await applyTagsToSales(
              values.tags,
              saleIds,
              activeAccountWebClientConfig
            );
            navigate(
              resolvePostSaleNavigation(result.sales, result.salesGroupId)
            );
          } else {
            showErrorDialog(
              'SaleClient.createRetailSaleGroup',
              {
                message: result.message ?? genericError,
                status: 400,
              } as ApiException,
              { trackErrorData: groupInput }
            );
          }
        },
        (error) => {
          showErrorDialog('SaleClient.createRetailSaleGroup', error, {
            trackErrorData: groupInput,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    }
  }, [
    activeAccountWebClientConfig,
    applyExternalPayment,
    genericError,
    getValues,
    isPartialPayment,
    listing,
    navigate,
    partialAmount,
    selectedBuyer,
    showErrorDialog,
  ]);

  const onPayAndComplete = useCallback(() => {
    const storedMethodId = selectedStoredPaymentMethodId;
    const values = getValues();
    // POS-9097: TRUE single-listing sale charges Adyen at the SALE level
    // (salesGroupId null, saleId set); multi-listing keeps the group-level charge.
    const isSingleListing = resolvesToSingleListing(values, listing);
    setIsLoading(true);

    tryInvokeApi(
      async () => {
        if (!saleIdRef.current) {
          // Create the sale as Unpaid (omit external fields); the Adyen session/
          // payment below settles it.
          const client = new SaleClient(activeAccountWebClientConfig);

          if (isSingleListing) {
            const input = buildRetailSaleInput(
              values,
              listing,
              selectedBuyer,
              false
            );
            const result = await client.createRetailSale(input);
            if (!result.saleId) return;

            saleIdRef.current = result.saleId;
            // A true single sale has no SalesGroup; keep the group refs null so the
            // Adyen session/navigation below key off the sale.
            salesGroupIdRef.current = null;
            salesRef.current = undefined;
            await applyTagsToSales(
              values.tags,
              [result.saleId],
              activeAccountWebClientConfig
            );
          } else {
            const groupInput = buildRetailSaleGroupInput(
              values,
              listing,
              selectedBuyer,
              false
            );
            const placeholderResult =
              await client.createRetailSaleGroup(groupInput);
            const placeholderSaleId = placeholderResult.sales?.[0]?.saleId;

            if (!placeholderResult.success || !placeholderSaleId) {
              showErrorDialog(
                'SaleClient.createRetailSaleGroup',
                {
                  message: placeholderResult.message ?? genericError,
                  status: 400,
                } as ApiException,
                { trackErrorData: groupInput }
              );
              return;
            }

            saleIdRef.current = placeholderSaleId;
            salesGroupIdRef.current = placeholderResult.salesGroupId;
            salesRef.current = placeholderResult.sales;
            const saleIds = placeholderResult.sales?.map((s) => s.saleId) ?? [];
            await applyTagsToSales(
              values.tags,
              saleIds,
              activeAccountWebClientConfig
            );
          }
        }

        // If partial payment, auto-compute proportional coverages
        let coverages: SaleLineItemCoverage[] | null = null;
        // Round to cents: the amount is a JS float summed from line amounts and
        // can carry a sub-cent artifact (e.g. 5.43 + 7.29 === 12.719999999999999).
        // Coverages are rounded per-line, so an un-rounded amount fails the
        // backend's exact "amount === sum(coverages)" guard in ValidateCoverages.
        let amount = roundToPrecision(getPaymentAmount(values), 2);
        if (isPartialPayment) {
          const client = new SaleClient(activeAccountWebClientConfig);
          amount = roundToPrecision(partialAmount, 2);
          if (isSingleListing) {
            // Single sale: compute coverages over its own unpaid lines.
            const saleDetails = await client.getSaleBySaleId(
              saleIdRef.current!
            );
            coverages = computeProportionalCoverages(amount, [saleDetails]);
          } else {
            const salesData = await client.getSalesInSalesGroup(
              salesGroupIdRef.current!
            );
            coverages = computeProportionalCoverages(amount, salesData);
          }
        }

        const merchantReference = getRetailMerchantReferenceId(
          saleIdRef.current
        );
        // The Adyen charge targets exactly one of saleId (single) / salesGroupId
        // (group). Compute both once so the stored-method and session branches agree.
        const chargeSaleId = isSingleListing ? saleIdRef.current : null;
        const chargeSalesGroupId = isSingleListing
          ? null
          : salesGroupIdRef.current;
        const successUrl = isSingleListing
          ? getSaleDetailsRelativeUrl(saleIdRef.current)
          : resolvePostSaleNavigation(
              salesRef.current,
              salesGroupIdRef.current!
            );

        try {
          if (storedMethodId) {
            const paymentStatus =
              await submitPaymentWithStoredMethod.mutateAsync({
                salesGroupId: chargeSalesGroupId,
                saleId: chargeSaleId,
                storedPaymentMethodId: storedMethodId,
                amount,
                currencyCode: values.currencyCode ?? 'USD',
                buyerEmail: values.buyerEmail ?? null,
                reference: merchantReference,
                lineItemCoverages: coverages,
              });
            if (!paymentStatus.settled) {
              showErrorDialog(
                'RetailSale.onPayAndComplete',
                {
                  message:
                    'Payment was received but is still being finalized. Please check the sale status in a moment.',
                },
                { trackErrorData: { saleId: saleIdRef.current } }
              );
              return;
            }
            navigate(successUrl);
          } else {
            const countryCode = getPaymentCountryCode(
              selectedBuyer,
              knownCountries.data
            );
            const sessionResponse = await createPaymentSession.mutateAsync({
              salesGroupId: chargeSalesGroupId,
              saleId: chargeSaleId,
              amount,
              currencyCode: values.currencyCode ?? 'USD',
              buyerEmail: values.buyerEmail ?? null,
              returnUrl: window.location.href,
              reference: merchantReference,
              countryCode,
              lineItemCoverages: coverages,
            });

            setAdyenSession({
              saleId: saleIdRef.current,
              salesGroupId: chargeSalesGroupId,
              sessionId: sessionResponse.sessionId,
              sessionData: sessionResponse.sessionData,
              clientKey: sessionResponse.clientKey,
              environment: sessionResponse.environment,
              merchantReference,
              countryCode,
              lineItemCoverages: coverages,
            });
          }
        } catch (error: any) {
          trackError('AdyenPayment.onError', new Error(error.message));
          navigate(successUrl);
        }
      },
      (error) => {
        showErrorDialog('RetailSale.onPayAndComplete', error, {
          trackErrorData: { listingId: listing?.id ?? values.listingId },
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    createPaymentSession,
    genericError,
    getValues,
    isPartialPayment,
    knownCountries.data,
    listing,
    navigate,
    partialAmount,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    showErrorDialog,
    submitPaymentWithStoredMethod,
    trackError,
  ]);

  const onAdyenPaymentCompleted = useCallback(
    async (result: AdyenPaymentCompletedResult) => {
      if (!adyenSession) return;

      if (result.resultCode !== 'Authorised') {
        setIsLoading(false);
        setAdyenSession(null);
        showErrorDialog(
          'RetailSale.onPayAndComplete',
          {
            message: `Payment not authorized. Result code: ${result.resultCode}`,
          },
          {
            trackErrorData: {
              resultCode: result.resultCode,
              saleId: adyenSession.saleId,
            },
          }
        );
        return;
      }

      await tryInvokeApi(
        async () => {
          const paymentStatus = await submitPaymentResult.mutateAsync({
            salesGroupId: adyenSession.salesGroupId,
            input: {
              resultCode: result.resultCode,
              sessionResult: result.sessionResult ?? null,
              merchantReference: adyenSession.merchantReference,
              pspReference: null,
              lineItemCoverages: adyenSession.lineItemCoverages,
            },
          });

          if (!paymentStatus.settled) {
            // Charge authorised at Adyen but POS settlement did not commit.
            // The AUTHORISATION webhook will retry settlement — show a transitional state.
            showErrorDialog(
              'RetailSale.onPayAndComplete',
              {
                message:
                  'Payment was received but is still being finalized. Please check the sale status in a moment.',
              },
              {
                trackErrorData: {
                  resultCode: paymentStatus.resultCode,
                  saleId: adyenSession.saleId,
                },
              }
            );
            return;
          }

          // POS-9097: a TRUE single sale has no SalesGroup — route to its own sale
          // detail; a group routes via resolvePostSaleNavigation.
          navigate(
            adyenSession.salesGroupId
              ? resolvePostSaleNavigation(
                  salesRef.current,
                  adyenSession.salesGroupId
                )
              : getSaleDetailsRelativeUrl(adyenSession.saleId)
          );
        },
        (error) => {
          showErrorDialog('RetailSale.onPayAndComplete', error, {
            trackErrorData: {
              listingId: listing?.id,
              saleId: adyenSession.saleId,
            },
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      adyenSession,
      listing?.id,
      navigate,
      setIsLoading,
      showErrorDialog,
      submitPaymentResult,
    ]
  );

  const onAdyenError = useCallback(
    (error: { message: string }) => {
      trackError('AdyenDropIn.onError', new Error(error.message));
      setAdyenSession(null);
    },
    [setAdyenSession, trackError]
  );

  const onUpsertBuyer = useCallback(async (): Promise<boolean> => {
    const values = getValues();

    const needsUpsert =
      !selectedBuyer ||
      !selectedBuyer.shippingAddressLine1 ||
      !selectedBuyer.shippingCity ||
      !selectedBuyer.shippingState ||
      !selectedBuyer.shippingZipCode ||
      !selectedBuyer.shippingCountry ||
      !selectedBuyer.billingAddressLine1 ||
      !selectedBuyer.billingCity ||
      !selectedBuyer.billingState ||
      !selectedBuyer.billingZipCode ||
      !selectedBuyer.billingCountry;

    if (!needsUpsert) return true;

    const buildAddress = (
      street: string | undefined,
      suite: string | undefined,
      city: string | undefined,
      state: string | undefined,
      zipCode: string | undefined,
      country: string | undefined
    ): GenericAddressDto | null => {
      if (!street && !state && !zipCode && !country) return null;
      return {
        genericAddressId: 0,
        addressLine1: street ?? null,
        addressLine2: suite ?? null,
        city: city ?? null,
        state: state ?? null,
        zipCode: zipCode ?? null,
        country: country ?? null,
      };
    };

    const shippingAddress = buildAddress(
      values.shippingStreet,
      values.shippingSuite,
      values.shippingCity,
      values.shippingState,
      values.shippingZipCode,
      values.shippingCountry
    );
    const billingAddress = values.billingAddressSameAsShipping
      ? shippingAddress
      : buildAddress(
          values.billingStreet,
          values.billingSuite,
          values.billingCity,
          values.billingState,
          values.billingZipCode,
          values.billingCountry
        );

    const dto: BuyerUpsertDto = {
      buyerEmail: selectedBuyer?.buyerEmail ?? values.buyerEmail!,
      buyerName: selectedBuyer?.buyerName ?? values.buyerName ?? null,
      buyerPhoneNumber:
        selectedBuyer?.buyerPhoneNumber ?? values.buyerPhone ?? null,
      internalNotes:
        selectedBuyer?.internalNotes ?? values.customerMessage ?? null,
      lastRetailActivityDate: null,
      shippingAddress,
      billingAddress,
      externalId: null,
    };

    setIsLoading(true);
    try {
      await upsertBuyer.mutateAsync(dto);

      setSelectedBuyer({
        ...(selectedBuyer ?? {}),
        buyerEmail: dto.buyerEmail,
        buyerName: dto.buyerName,
        buyerPhoneNumber: dto.buyerPhoneNumber,
        internalNotes: selectedBuyer?.internalNotes ?? null,
        lastRetailActivityDate: selectedBuyer?.lastRetailActivityDate ?? null,
        shippingAddressLine1: shippingAddress?.addressLine1 ?? null,
        shippingAddressLine2: shippingAddress?.addressLine2 ?? null,
        shippingCity: shippingAddress?.city ?? null,
        shippingState: shippingAddress?.state ?? null,
        shippingZipCode: shippingAddress?.zipCode ?? null,
        shippingCountry: shippingAddress?.country ?? null,
        billingAddressLine1: billingAddress?.addressLine1 ?? null,
        billingAddressLine2: billingAddress?.addressLine2 ?? null,
        billingCity: billingAddress?.city ?? null,
        billingState: billingAddress?.state ?? null,
        billingZipCode: billingAddress?.zipCode ?? null,
        billingCountry: billingAddress?.country ?? null,
        cardBrand: selectedBuyer?.cardBrand ?? null,
        cardSummary: selectedBuyer?.cardSummary ?? null,
        expiryMonth: selectedBuyer?.expiryMonth ?? null,
        expiryYear: selectedBuyer?.expiryYear ?? null,
        retailCreditBalance: selectedBuyer?.retailCreditBalance ?? 0,
        creditCurrencyCode: selectedBuyer?.creditCurrencyCode ?? null,
        saleIds: selectedBuyer?.saleIds ?? null,
        salesGroupIds: selectedBuyer?.salesGroupIds ?? null,
        externalId: selectedBuyer?.externalId ?? null,
      });

      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getValues, selectedBuyer, setIsLoading, setSelectedBuyer, upsertBuyer]);

  return {
    disabled: disableEditing,
    adyenSession,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onCompleteExternalPayment,
    onPayAndComplete,
    onAdyenPaymentCompleted,
    onAdyenError,
  };
};
